import { Form, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import i18n from 'i18next';

import { Paper } from 'componets/ui';
import { FormItem, SelectField } from 'componets/form';
import { RangeDatePicker } from 'componets/common';

import { FieldData, IRangePicker } from 'models';
import { DATE_FORMATTER, Rules } from 'utils/constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectUser } from 'selectors';
import Utils from 'utils/utils';

import { getSummary } from 'features/statement/actions';

import s from '../index.module.scss';

const { t } = i18n;
export const AccountSummaryTable = () => {
	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);

	const dispatch = useAppDispatch();

	const summary = useAppSelector((state) => state.statement.summary);
	const user = useAppSelector(selectUser);

	const [range, setRange] = useState<IRangePicker | undefined>({
		startDate: moment().startOf('month'),
		endDate: moment(),
	});

	const onFinish = (v: IRangePicker) => {
		dispatch(
			getSummary({
				startDate: v ? moment(v.startDate).format(DATE_FORMATTER) : '',
				endDate: v ? moment(v.endDate).format(DATE_FORMATTER) : '',
			}),
		);
	};

	const { t } = useTranslation();

	return (
		<div className="accountSummary__summary">
			<div className="accountSummary__tableRow">
				<Form form={form} onFieldsChange={setFields} fields={fields}>
					<FormItem className={s.rangeInput} name="range" rules={[Rules.REQUIRED_SELECT]}>
						<RangeDatePicker
							onChange={(v) => {
								setRange(v);
								onFinish(v);
								form.resetFields();
							}}
							range={range}
							minDate={moment(user.registrationDate)}
							maxDate={moment().endOf('day')}
						/>
					</FormItem>

					<FormItem name="period" className="accountSummary__select">
						<SelectField
							options={OPTIONS}
							placeholder={t('selectPeriodText')}
							onChange={(v) => {
								const period =
									v === '7'
										? {
												startDate: moment(user.registrationDate),
												endDate: moment(),
										  }
										: (RANGES[v] as IRangePicker);

								setRange(period);
								onFinish(period);
							}}
						/>
					</FormItem>
				</Form>
			</div>
			<Paper styleName="accountSummary__table">
				<h2>{t('accountSummaryText')}</h2>
				<div>
					{Object.keys(DETAILS).map((item) => {
						const isDate = item === 'openingBalance' || item === 'closingBalance';
						const isOpening = item === 'openingBalance';
						const isClosing = item === 'closingBalance';
						const isAdditional = ADDITIONAL_PARAMS.includes(item);

						if (isAdditional && !summary[item]) return '';

						return (
							<div key={item} className="accountSummary__row accountSummary__row--summary">
								<Typography.Text type={isDate ? undefined : 'secondary'}>
									{t(DETAILS[item])} {isOpening && range?.startDate.format('YYYY-MM-DD')}{' '}
									{isClosing && range?.endDate.format('YYYY-MM-DD')}
								</Typography.Text>
								<div>
									<Typography.Text>
										{summary[item] >= 0 && !isOpening && !isClosing ? '+' : ''}
										{Utils.moneyFormat(summary[item] || 0)}
									</Typography.Text>
								</div>
							</div>
						);
					})}
				</div>
			</Paper>
		</div>
	);
};

const RANGES: { [key: string]: IRangePicker } = {
	1: { startDate: moment(), endDate: moment() },
	2: { startDate: moment().subtract(6, 'd'), endDate: moment() },
	3: { startDate: moment().startOf('month'), endDate: moment() },
	4: {
		startDate: moment().subtract(1, 'months').startOf('month'),
		endDate: moment().subtract(1, 'months').endOf('month'),
	},
	5: { startDate: moment().startOf('year'), endDate: moment() },
	6: {
		startDate: moment().subtract(1, 'year').startOf('year'),
		endDate: moment().subtract(1, 'year').endOf('year'),
	},
	7: { startDate: moment(), endDate: moment() },
};

const OPTIONS = [
	{ value: '1', label: t('todayText') },
	{ value: '2', label: t('lastSevenDaysText') },
	{ value: '3', label: t('thisMonthSelectText') },
	{ value: '4', label: t('lastMonthText') },
	{ value: '5', label: t('thisYearText') },
	{ value: '6', label: t('previousYearText') },
	{ value: '7', label: t('allTimeText') },
];

const DETAILS: any = {
	openingBalance: t('openingBalanceText'),
	INTEREST: t('interestIncomeText'),
	COMPENSATION_INTEREST: t('additionalInterestText'),
	TAX_WITHHOLDING: t('taxWithholdingText'),
	PRINCIPAL: t('principalRepaymentsText'),
	INVESTMENT: t('investmentsText'),
	DEPOSIT: t('depositsText'),
	WITHDRAWAL: t('withdrawalsText'),
	DEPOSIT_CORRECTION: t('depositCorrectionAccountText'),
	INVESTMENT_PURCHASE_DIFF: t('investmentPurchaseText'),
	INVESTMENT_SALE_DIFF: t('investmentSaleDifferenceText'),
	INVESTMENT_SALE_FEE: t('investmentSaleFeeText'),
	REFERRAL_FEE: t('referralFeeSummaryText'),
	WITHDRAWAL_REFUND: t('withdrawalReturnAccountText'),
	closingBalance: t('closingBalanceText'),
};

const ADDITIONAL_PARAMS = [
	'COMPENSATION_INTEREST',
	'DEPOSIT_CORRECTION',
	'INVESTMENT_PURCHASE_DIFF',
	'INVESTMENT_SALE_DIFF',
	'INVESTMENT_SALE_FEE',
	'REFERRAL_FEE',
	'WITHDRAWAL_REFUND',
];
